'use client'

import { ROUTES } from '@/core/constants/routes'
import { reportError } from '@/core/lib/utils/report-error'
import {
  MaintainanceError,
  UnauthorizedError,
} from '@/core/types/errors/http-error'
import { Button } from '@/core/ui/atoms/button'
import Image from 'next/image'
import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { useEffect } from 'react'

type Props = {
  error: Error & { digest?: string }
  reset: () => void
}
export const AppError: React.FC<Props> = ({ error, reset }) => {
  const router = useRouter()
  const title =
    error instanceof MaintainanceError || error.name === 'MaintainanceError'
      ? 'Maintenance in Progress'
      : 'Oops! Something went wrong'

  const description =
    error instanceof MaintainanceError || error.name === 'MaintainanceError'
      ? 'Our servers are currently undergoing maintenance. Please check back shortly.'
      : 'We encountered an unexpected issue while processing your request.'

  useEffect(() => {
    if (
      error instanceof UnauthorizedError ||
      error.name === 'UnauthorizedError'
    ) {
      router.replace(ROUTES.home)
    }
  }, [error, router])

  useEffect(() => {
    const controller = new AbortController()

    if ('reported' in error && error.reported) {
      return
    }
    reportError(error, controller)

    return () => controller.abort()
  }, [error])

  return (
    <>
      <div className='flex h-screen w-screen items-center justify-center p-4'>
        <div className='border-grey-20 flex h-full w-full max-w-4xl flex-col justify-center space-y-8 overflow-y-auto rounded-lg border-1 p-6'>
          <div className='flex flex-col items-center text-center'>
            <div className='relative h-80 min-h-40 w-full'>
              <Image
                src='/images/errors/500.svg'
                fill
                alt='500 | Something went wrong'
              />{' '}
            </div>
            <h2 className='text-navy mt-6 text-3xl font-extrabold'>{title}</h2>
            <p className='mt-4 text-lg text-gray-600'>{description}</p>
          </div>

          <div className='flex flex-col justify-center gap-3 sm:flex-row'>
            <Button onClick={reset}>Try Again</Button>

            <Button component={Link} href={ROUTES.home} variant='outlined'>
              Go Home
            </Button>
          </div>

          <div className='text-center'>
            <p className='text-sm text-gray-500'>
              Need additional help?{' '}
              <Link
                href={ROUTES.contactUs}
                className='text-sky-blue font-medium hover:underline'
              >
                Contact us
              </Link>
            </p>
          </div>
        </div>
      </div>
    </>
  )
}
